import { useEffect, useState } from "react";
import { TextInput, View } from "react-native";
import { Calendar } from "lucide-react-native";
import {
  formatDateTyping,
  isoToDisplay,
  parseTypedToIso,
  placeholderFor,
  useDateFormat,
} from "@src/packages/utils/useDateFormat";
import { colors } from "../theme/tokens";
import { MoonFieldInput } from "./MoonFieldInput";
import { useInputFontSize } from "../utils/inputFontSize";

/**
 * Desktop-web typeable date input.
 *
 * Reverted from `react-native-advanced-input-mask` (commit 49c95b94e)
 * because the masked variant used `defaultValue` (uncontrolled) and only
 * emitted `onChange` when its internal `complete` flag fired AND
 * `parseTypedToIso` returned a non-empty string. If either failed —
 * mask quirk, format mismatch, partial input — the parent state stayed
 * empty even though the user typed a full date, leaving downstream
 * "Next" / "Review" buttons greyed (UAT issue #103 follow-up: the
 * AddAppointment Review button wouldn't enable after typing a date).
 *
 * Controlled typing instead: `value={display}`, format-on-keystroke via
 * `formatDateTyping`, emit ISO (or `""`) on every change so parent
 * state always reflects what's typed.
 */
export function DesktopMaskedDateInput({
  value,
  onChange,
  placeholder,
  hasError,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  hasError: boolean;
}) {
  const { dateFormat } = useDateFormat();
  const fontSize = useInputFontSize();
  const [display, setDisplay] = useState(() => isoToDisplay(value, dateFormat));

  // Keep the displayed text in sync when the parent's ISO value changes
  // (e.g. on edit-mode mount or an external reset). The dependency on
  // `value` is exactly the right signal — within a single typing
  // session `value` only changes after we ourselves emit a valid ISO,
  // so this won't fight the user mid-keystroke.
  useEffect(() => {
    setDisplay((prev) => {
      const next = isoToDisplay(value, dateFormat);
      return prev === next ? prev : next;
    });
  }, [value, dateFormat]);

  return (
    <MoonFieldInput hasError={hasError} paddingY={2}>
      <View
        style={{
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: 10,
        }}
      >
        <Calendar size={13} color={colors.textLight} strokeWidth={1.5} />
        <TextInput
          value={display}
          onChangeText={(input) => {
            const formatted = formatDateTyping(input, dateFormat, display);
            setDisplay(formatted);
            const iso = parseTypedToIso(formatted, dateFormat);
            // Only propagate a complete ISO date, or an explicit clear.
            // Emitting the intermediate "" on every keystroke lets callers
            // like PersonalInfo PATCH `dateOfBirth: null` mid-typing, which
            // wipes the previously-saved DOB before the user finishes.
            if (iso) {
              onChange(iso);
            } else if (formatted === "") {
              onChange("");
            }
          }}
          placeholder={placeholder ?? placeholderFor(dateFormat)}
          placeholderTextColor={colors.textFaint}
          inputMode="numeric"
          maxLength={10}
          autoComplete="off"
          autoCorrect={false}
          spellCheck={false}
          style={{
            flex: 1,
            marginLeft: 10,
            fontSize,
            color: colors.text,
            fontFamily: "DMSans_400Regular",
            outlineStyle: "none",
          }}
        />
      </View>
    </MoonFieldInput>
  );
}
